import React, { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { apiGet } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';

const CATEGORY_LABELS = {
  most_total_points: { icon: '🏆', labelKey: 'hof_most_total_points', tipKey: 'hof_most_total_points_tip', unit: 'points' },
  best_attacker: { icon: '🗡️', labelKey: 'hof_best_attacker', tipKey: 'hof_best_attacker_tip', unit: 'stars' },
  best_defender: { icon: '🛡️', labelKey: 'hof_best_defender', tipKey: 'hof_best_defender_tip', unit: 'stars' },
  most_donations: { icon: '💝', labelKey: 'hof_most_donations', tipKey: 'hof_most_donations_tip', unit: 'donations' },
};

function CwlBonusSection({ t, allEntries, maxScore, medalIcon, medalColor }) {
  const [tipOpen, setTipOpen] = useState(false);
  return (
    <div className="hof-cwl-wrapper">
      <div className="hof-cwl-banner">
        <span className="hof-cwl-banner-gem">💎</span>
        <span className="hof-cwl-banner-title">{t('hof_cwl_bonus_candidates')}</span>
        <span className="hof-cwl-banner-count">{allEntries.length} players</span>
        <span
          className={`hof-cwl-info-btn${tipOpen ? ' active' : ''}`}
          onClick={() => setTipOpen(v => !v)}
          title={t('hof_cwl_bonus_tip')}
        >ℹ</span>
      </div>
      {tipOpen && (
        <div className="hof-cwl-tooltip">
          {t('hof_cwl_bonus_tip')}
        </div>
      )}
      <div className="hof-cwl-card">
        <div className="hof-cwl-rest">
          {allEntries.map(entry => (
            <div key={entry.rank} className="hof-cwl-row">
              <span
                className="hof-cwl-row-rank"
                style={medalColor[entry.rank] ? { color: medalColor[entry.rank] } : {}}
              >
                {medalIcon[entry.rank] || `#${entry.rank}`}
              </span>
              <span className="hof-cwl-row-name">{entry.player_name}</span>
              {entry.value > 0 && (
                <>
                  <div className="hof-cwl-row-bar-wrap">
                    <div
                      className="hof-cwl-row-bar-fill"
                      style={{ width: `${(entry.value / maxScore) * 100}%` }}
                    />
                  </div>
                  <span className="hof-cwl-row-score">{Math.round(entry.value)}</span>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function HallOfFame({ t, slug, formatSeasonLabel }) {
  const { data: rawSeasons, isError: error, isLoading } = useQuery({
    queryKey: ['halloffame', slug],
    queryFn: () => apiGet(`/v1/clan/${slug}/halloffame`),
    enabled: !!slug,
    placeholderData: keepPreviousData,
  });
  const seasons = rawSeasons || null;

  if (error) {
    return (
      <div id="halloffamePage">
        <h1 className="text-2xl md:text-3xl mb-6">🏆 <span>{t('nav_hall_of_fame')}</span></h1>
        <div id="hallOfFameContainer" className="text-center py-12 text-red-400">{t('error_loading_data')}</div>
      </div>
    );
  }

  if (seasons === null) {
    return (
      <div id="halloffamePage">
        <h1 className="text-2xl md:text-3xl mb-6">🏆 <span>{t('nav_hall_of_fame')}</span></h1>
        <div id="hallOfFameContainer" className="bg-gray-800 rounded-xl">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!seasons || seasons.length === 0) {
    return (
      <div id="halloffamePage">
        <h1 className="text-2xl md:text-3xl mb-6">🏆 <span>{t('nav_hall_of_fame')}</span></h1>
        <div id="hallOfFameContainer">
          <div className="text-center py-12 text-gray-400">
            <div className="text-4xl mb-4">🏆</div>
            <div className="text-lg">{t('hof_no_entries')}</div>
            <div className="text-sm mt-2">{t('hof_no_entries_desc')}</div>
          </div>
        </div>
      </div>
    );
  }

  const fmtVal = (v, unit) => {
    if (unit === 'stars') return `${Number(v).toFixed(2)} ⭐`;
    if (unit === 'donations') return `${Math.round(v).toLocaleString('de-CH')} ${t('troops_label')}`;
    return `${Math.round(v)} ${t('points_label')}`;
  };

  return (
    <div id="halloffamePage">
      <h1 className="text-2xl md:text-3xl mb-6">🏆 <span>{t('nav_hall_of_fame')}</span></h1>
      <div id="hallOfFameContainer">
        {seasons.map((season) => {
          const label = formatSeasonLabel(season.season);
          const categories = {};
          for (const entry of (season.season_end || [])) {
            if (!categories[entry.category]) categories[entry.category] = [];
            categories[entry.category].push(entry);
          }

          return (
            <div key={season.season} className="hof-season">
              <div className="hof-season-header">📅 {label}</div>

              {season.season_end && season.season_end.length > 0 && (
                <div className="hof-awards-wrapper">
                  <div className="hof-awards-banner">
                    <span className="hof-awards-banner-icon">🌟</span>
                    <span className="hof-awards-banner-title">{t('hof_season_awards')}</span>
                    <span className="hof-awards-banner-count">{Object.keys(categories).length} categories</span>
                  </div>
                  <div className="hof-awards-body">
                    <div className="hof-awards-grid">
                      {Object.entries(categories).map(([cat, entries]) => {
                        const info = CATEGORY_LABELS[cat];
                        if (!info) return null;
                        entries.sort((a, b) => (a.rank || 0) - (b.rank || 0));

                        return (
                          <div key={cat} className="hof-award-card" title={t(info.tipKey)}>
                            <div className="hof-award-icon">{info.icon}</div>
                            <div className="hof-award-category">{t(info.labelKey)}</div>
                            <div className="hof-award-podium">
                              {entries[0] && (
                                <div className="hof-podium-top">
                                  <div className="hof-podium-1">
                                    <div className="hof-podium-rank">🥇</div>
                                    <div className="hof-podium-name">{entries[0].player_name}</div>
                                    <div className="hof-podium-value">{fmtVal(entries[0].value, info.unit)}</div>
                                  </div>
                                </div>
                              )}
                              {(entries[1] || entries[2]) && (
                                <div className="hof-podium-bottom">
                                  {entries[1] && (
                                    <div className="hof-podium-2">
                                      <div className="hof-podium-rank">🥈</div>
                                      <div className="hof-podium-name">{entries[1].player_name}</div>
                                      <div className="hof-podium-value">{fmtVal(entries[1].value, info.unit)}</div>
                                    </div>
                                  )}
                                  {entries[2] && (
                                    <div className="hof-podium-3">
                                      <div className="hof-podium-rank">🥉</div>
                                      <div className="hof-podium-name">{entries[2].player_name}</div>
                                      <div className="hof-podium-value">{fmtVal(entries[2].value, info.unit)}</div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {season.clan_performance && (() => {
                const p = season.clan_performance;
                const r = 28;
                const circ = 2 * Math.PI * r;
                const dash = circ * (p.win_rate / 100);
                return (
                  <div className="hof-perf-wrapper">
                    <div className="hof-perf-banner">
                      <span className="hof-perf-banner-icon">⚔️</span>
                      <span className="hof-perf-banner-title">{t('hof_clan_performance')}</span>
                      <div className="hof-perf-war-pills">
                        <span className="hof-perf-pill hof-perf-pill-cw">{p.cw_wars} {t('hof_perf_cw')}</span>
                        <span className="hof-perf-pill hof-perf-pill-cwl">{p.cwl_wars} {t('hof_perf_cwl')}</span>
                      </div>
                    </div>

                    <div className="hof-perf-card">
                      <div className="hof-perf-hero">
                        <div className="hof-perf-dial-wrap">
                          <svg className="hof-perf-dial" viewBox="0 0 70 70">
                            <circle cx="35" cy="35" r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="6" />
                            <circle
                              cx="35" cy="35" r={r} fill="none"
                              stroke="url(#winGrad)" strokeWidth="6"
                              strokeLinecap="round"
                              strokeDasharray={`${dash} ${circ}`}
                              transform="rotate(-90 35 35)"
                            />
                            <defs>
                              <linearGradient id="winGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#22c55e" />
                                <stop offset="100%" stopColor="#4ade80" />
                              </linearGradient>
                            </defs>
                          </svg>
                          <div className="hof-perf-dial-inner">
                            <span className="hof-perf-dial-pct">{p.win_rate}%</span>
                            <span className="hof-perf-dial-sub">{t('hof_perf_win_rate')}</span>
                          </div>
                        </div>

                        <div className="hof-perf-record-col">
                          <div className="hof-perf-total-wars">
                            <span className="hof-perf-total-num">{p.total_wars}</span>
                            <span className="hof-perf-total-lbl">{t('hof_perf_wars')}</span>
                          </div>
                          <div className="hof-perf-wld">
                            <div className="hof-perf-wld-item hof-perf-wld-w">
                              <span className="hof-perf-wld-num">{p.wins}</span>
                              <span className="hof-perf-wld-lbl">{t('hof_perf_wins')}</span>
                            </div>
                            <div className="hof-perf-wld-sep" />
                            <div className="hof-perf-wld-item hof-perf-wld-l">
                              <span className="hof-perf-wld-num">{p.losses}</span>
                              <span className="hof-perf-wld-lbl">{t('hof_perf_losses')}</span>
                            </div>
                            <div className="hof-perf-wld-sep" />
                            <div className="hof-perf-wld-item hof-perf-wld-d">
                              <span className="hof-perf-wld-num">{p.draws}</span>
                              <span className="hof-perf-wld-lbl">{t('hof_perf_draws')}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="hof-perf-divider" />

                      <div className="hof-perf-stats-row">
                        <div className="hof-perf-stat-group hof-perf-atk-group">
                          <div className="hof-perf-group-header">⚔️ {t('hof_perf_attacks')}</div>
                          <div className="hof-perf-stat-line">
                            <span className="hof-perf-stat-line-lbl">{t('hof_perf_attacks')}</span>
                            <span className="hof-perf-stat-line-val hof-perf-atk-val">{p.total_attacks}</span>
                          </div>
                          <div className="hof-perf-stat-line">
                            <span className="hof-perf-stat-line-lbl">{t('hof_perf_missed')}</span>
                            <span className="hof-perf-stat-line-val hof-perf-miss-val">{p.missed_attacks}</span>
                          </div>
                          <div className="hof-perf-stat-line">
                            <span className="hof-perf-stat-line-lbl">⭐ {t('hof_perf_avg_atk_stars')}</span>
                            <span className="hof-perf-stat-line-val hof-perf-stars-val">{p.avg_attack_stars}</span>
                          </div>
                          <div className="hof-perf-star-bar">
                            <div className="hof-perf-star-fill hof-perf-star-atk" style={{ width: `${(p.avg_attack_stars / 3) * 100}%` }} />
                          </div>
                        </div>

                        <div className="hof-perf-group-divider" />

                        <div className="hof-perf-stat-group hof-perf-def-group">
                          <div className="hof-perf-group-header">🛡️ {t('hof_perf_defenses')}</div>
                          <div className="hof-perf-stat-line">
                            <span className="hof-perf-stat-line-lbl">{t('hof_perf_defenses')}</span>
                            <span className="hof-perf-stat-line-val hof-perf-def-val">{p.total_defenses}</span>
                          </div>
                          <div className="hof-perf-stat-line">
                            <span className="hof-perf-stat-line-lbl">💥 {t('hof_perf_avg_destruction')}</span>
                            <span className="hof-perf-stat-line-val hof-perf-dest-val">{p.avg_destruction}%</span>
                          </div>
                          <div className="hof-perf-stat-line">
                            <span className="hof-perf-stat-line-lbl">⭐ {t('hof_perf_avg_def_stars')}</span>
                            <span className="hof-perf-stat-line-val hof-perf-defstars-val">{p.avg_defense_stars}</span>
                          </div>
                          <div className="hof-perf-star-bar">
                            <div className="hof-perf-star-fill hof-perf-star-def" style={{ width: `${(p.avg_defense_stars / 3) * 100}%` }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {season.cwl_bonus && season.cwl_bonus.length > 0 && (() => {
                const allEntries = [...season.cwl_bonus].sort((a, b) => (a.rank || 0) - (b.rank || 0));
                const maxScore = allEntries[0]?.value || 1;
                const medalIcon = { 1: '🥇', 2: '🥈', 3: '🥉' };
                const medalColor = { 1: '#fbbf24', 2: '#94a3b8', 3: '#d97706' };
                return (
                  <CwlBonusSection
                    key={season.season}
                    t={t}
                    allEntries={allEntries}
                    maxScore={maxScore}
                    medalIcon={medalIcon}
                    medalColor={medalColor}
                  />
                );
              })()}
            </div>
          );
        })}
      </div>
    </div>
  );
}
